import type { UIMessage } from 'ai';
import type {
  AskUserToolInputDTO,
  AskUserToolOutputDTO,
} from '@shared/schemas/chat-ask-user';
import type {
  DocumentGenerationToolInputDTO,
  DocumentGenerationToolOutputDTO,
} from '@shared/schemas/document-generation';
import type {
  ChatDocumentDTO,
  ChatDocumentDeltaDTO,
  ChatMessageFilePartDTO,
  ChatSourceReferenceDTO,
  ConversationDTO,
  ConversationMessagesDTO,
  PersistedChatMessageDTO,
} from '@shared/schemas/chat';
import type { ChunkAnchor } from '@shared/schemas/rag-anchors';
import type { WebResearchActivityDTO } from '@shared/schemas/web-tools';

export type ChatRagCitationDTO = {
  anchor?: ChunkAnchor | null;
  citationBlockId?: string;
  citationDocumentId?: string;
  citationPage?: number;
  excerpt?: string;
  filename?: string;
  mediaType: string;
  references?: ChatSourceReferenceDTO[];
  sourceId: string;
  title: string;
  type: 'source-document';
  url?: string;
};

export type ChatUIDataTypes = {
  conversation: {
    conversationId: string;
    ragEnabled: boolean;
    title: string | null;
    webSearchEnabled: boolean;
  };
  'chat-title': {
    conversationId: string;
    title: string | null;
  };
  'document-preview': ChatDocumentDTO;
  'document-open': ChatDocumentDTO;
  'document-delta': ChatDocumentDeltaDTO;
  'document-finish': ChatDocumentDTO;
  'rag-citations': {
    citations: ChatRagCitationDTO[];
  };
  'rag-empty': {
    actionHref: '/documents';
    actionLabel: string;
  };
  'web-research-activity': WebResearchActivityDTO;
  'document-generation-pending': { title?: string };
};

export type ChatUITools = {
  ask_user: {
    input: AskUserToolInputDTO;
    output: AskUserToolOutputDTO;
  };
  generate_document: {
    input: DocumentGenerationToolInputDTO;
    output: DocumentGenerationToolOutputDTO;
  };
  revise_document: {
    input: unknown;
    output: DocumentGenerationToolOutputDTO;
  };
};

export type ChatUIMessageMetadata = {
  createdAt?: string;
};

export type ChatUIMessage = UIMessage<ChatUIMessageMetadata, ChatUIDataTypes, ChatUITools>;

export type ChatUIFilePart = ChatMessageFilePartDTO;

export type ConversationListResponse = {
  items: ConversationDTO[];
  meta: {
    page: number;
    pageCount: number;
    pageSize: number;
    total: number;
  };
};

export function toChatUiMessage(message: PersistedChatMessageDTO): ChatUIMessage {
  return {
    id: message.id,
    metadata: message.createdAt ? { createdAt: message.createdAt } : undefined,
    parts: message.parts as ChatUIMessage['parts'],
    role: message.role,
  };
}

export function toChatUiMessages(payload: ConversationMessagesDTO) {
  return payload.messages.map(toChatUiMessage);
}
